import { useEffect, useMemo, useState } from "react";
import { Card, DatePicker, Empty, Select } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import groupBy from "lodash.groupby";
import keyBy from "lodash.keyby";

import { teamSelectors } from "@fitness-app/app-store";
import { TeamMemberRole, TeamMemberStatus } from "@fitness-app/data-models/entities/TeamMember";

import { useAppSelector } from "~/store/initializeStore";
import { useTeamMemberMonthStats } from "./hooks/useTeamMemberMonthStats";
import { TrainerMonthClientsChart } from "./TrainerMonthClientsChart";

const monthFormat = "YYYY/MM";

export const ALL_TRAINERS_FILTER_ID = "all";

export default function TeamMembersStats() {
  const [selectedMonth, setMonth] = useState<Dayjs>(dayjs().subtract(1, "month"));
  const [selectedTrainers, setSelectedTrainers] = useState<string[]>([]);
  const trainers = useAppSelector(teamSelectors.getTrainersTeam) ?? [];
  const { data: stats, isLoading } = useTeamMemberMonthStats({
    month: selectedMonth,
    trainerIds: selectedTrainers,
  });

  const handleChange = (value: string[]) => {
    if (value.length > 1) {
      setSelectedTrainers(value.filter((item) => item !== ALL_TRAINERS_FILTER_ID));
    } else {
      setSelectedTrainers(value);
    }
  };

  const activeTrainers = useMemo(
    () =>
      trainers.filter(
        (trainer) =>
          trainer.status === TeamMemberStatus.ACTIVE &&
          (trainer.role === TeamMemberRole.REGULAR_TRAINER || trainer.role === TeamMemberRole.SHARED_TRAINER),
      ),
    [trainers],
  );

  useEffect(() => {
    if (activeTrainers.length > 0) {
      setSelectedTrainers([activeTrainers[0]?.id ?? ALL_TRAINERS_FILTER_ID]);
    }
  }, [activeTrainers.length]);

  const trainersList = useMemo(() => {
    const list = activeTrainers.map((trainer) => ({
      label: `${trainer.user?.firstName} ${trainer.user?.lastName}`,
      value: trainer.id,
    }));

    return [{ label: "Wszyscy trenerzy", value: ALL_TRAINERS_FILTER_ID }, ...list];
  }, [activeTrainers]);

  const byId = useMemo(() => keyBy(activeTrainers, "id"), [activeTrainers]);

  const listToRender = useMemo(() => {
    if (selectedTrainers.length === 1 && selectedTrainers[0] === ALL_TRAINERS_FILTER_ID) {
      return activeTrainers;
    }
    return selectedTrainers.map((trainerId) => byId[trainerId]).filter((trainer) => trainer !== undefined);
  }, [selectedTrainers, byId, activeTrainers]);

  const byTrainerId = useMemo(() => groupBy(stats, "trainerId"), [stats]);

  return (
    <Card
      title="Rozliczenia trenerów"
      loading={isLoading}
      extra={
        <div className="flex justify-end gap-3">
          <Select
            mode="multiple"
            defaultValue={[ALL_TRAINERS_FILTER_ID]}
            onChange={handleChange}
            value={selectedTrainers}
            style={{ minWidth: 320, maxWidth: 500 }}
            options={trainersList}
          />
          <DatePicker
            value={selectedMonth}
            format={monthFormat}
            picker="month"
            onChange={setMonth}
            disabledDate={(p) => p.isAfter(dayjs())}
          />
        </div>
      }
    >
      {listToRender.length === 0 && <Empty description="Wybierz trenerów do wyświetlenia" />}
      <div className="space-y-3">
        {listToRender.map((trainer) => (
          <Card type="inner" key={trainer.id} title={`${trainer.user?.firstName} ${trainer.user?.lastName}`}>
            <TrainerMonthClientsChart
              trainerName={`${trainer.user?.firstName} ${trainer.user?.lastName}`}
              stats={byTrainerId[trainer.id] || []}
              selectedMonth={selectedMonth}
            />
          </Card>
        ))}
      </div>
    </Card>
  );
}
